<template>
  <NavBar v-if="userLoaded" pageName="Service Quarantine Durations"></NavBar>

  <v-container style="margin-top: 185px;">
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-btn variant="text" size="small" @click="$router.push('/settings/services')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Services Settings
    </v-btn>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" color="primary" class="mb-4">
      <v-tab value="service-durations">
        <v-icon class="mr-2">mdi-shield-lock-outline</v-icon>
        Service Durations
        <v-chip size="x-small" class="ml-2" variant="tonal">{{ serviceDurations.length }}</v-chip>
      </v-tab>
      <v-tab value="cs-durations">
        <v-icon class="mr-2">mdi-cog-transfer-outline</v-icon>
        CS Overrides
        <v-chip size="x-small" class="ml-2" variant="tonal">{{ csDurations.length }}</v-chip>
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- ==================== TAB 1: Service Quarantine Durations ==================== -->
      <v-window-item value="service-durations">
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6">
            <p class="text-caption text-grey">Configure default quarantine duration per service and property type</p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end gap-2">
            <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
              <v-icon class="mr-2">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddServiceDialog">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Duration
            </v-btn>
          </v-col>
        </v-row>

        <v-card>
          <v-card-text>
            <v-data-table
              :headers="serviceTableHeaders"
              :items="serviceDurations"
              :loading="loading"
              :items-per-page="20"
              item-value="id"
              class="elevation-1"
            >
              <template #[`item.service_name`]="{ item }">
                <div>
                  <strong>{{ item.service_name }}</strong>
                  <div class="text-caption text-grey">ID: {{ item.service_id }}</div>
                </div>
              </template>
              <template #[`item.property_type`]="{ item }">
                <v-chip
                  :color="propertyTypeColor(item.property_type)"
                  size="small"
                >
                  {{ item.property_type }}
                </v-chip>
              </template>
              <template #[`item.quarantine_display`]="{ item }">
                <strong>{{ item.quarantine_value }}</strong> {{ item.billing_period_name }}
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip
                  :color="item.active ? 'success' : 'error'"
                  size="small"
                  @click="toggleServiceActive(item)"
                  style="cursor: pointer;"
                >
                  {{ item.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn size="x-small" color="primary" class="mr-1" @click="openEditServiceDialog(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn size="x-small" color="error" @click="confirmDeleteService(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <v-alert type="info" class="ma-2">No service quarantine durations configured</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ==================== TAB 2: CS Quarantine Duration Overrides ==================== -->
      <v-window-item value="cs-durations">
        <v-row class="align-center mb-4">
          <v-col cols="12" md="6">
            <p class="text-caption text-grey">Override quarantine duration for specific Client Services</p>
          </v-col>
          <v-col cols="12" md="6" class="d-flex justify-end gap-2">
            <v-btn color="primary" variant="outlined" :loading="loadingCs" @click="loadCsData">
              <v-icon class="mr-2">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddCsDialog">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Override
            </v-btn>
          </v-col>
        </v-row>

        <v-card>
          <v-card-text>
            <v-data-table
              :headers="csTableHeaders"
              :items="csDurations"
              :loading="loadingCs"
              :items-per-page="20"
              item-value="id"
              class="elevation-1"
            >
              <template #[`item.client_service_id`]="{ item }">
                <div>
                  <strong>CS #{{ item.client_service_id }}</strong>
                  <div class="text-caption text-grey">{{ item.client_name }} - {{ item.service_name }}</div>
                </div>
              </template>
              <template #[`item.property_type`]="{ item }">
                <v-chip
                  :color="propertyTypeColor(item.property_type)"
                  size="small"
                >
                  {{ item.property_type }}
                </v-chip>
              </template>
              <template #[`item.quarantine_display`]="{ item }">
                <strong>{{ item.quarantine_value }}</strong> {{ item.billing_period_name }}
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip
                  :color="item.active ? 'success' : 'error'"
                  size="small"
                  @click="toggleCsActive(item)"
                  style="cursor: pointer;"
                >
                  {{ item.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn size="x-small" color="primary" class="mr-1" @click="openEditCsDialog(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn size="x-small" color="error" @click="confirmDeleteCs(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <v-alert type="info" class="ma-2">No CS quarantine duration overrides configured</v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Service Duration Dialog -->
    <ServiceQuarantineDurationDialog
      :show="serviceFormDialog"
      :is-editing="isEditingService"
      :form-data="serviceFormData"
      :services="services"
      :billing-periods="billingPeriods"
      :saving="savingService"
      @close="serviceFormDialog = false"
      @save="saveServiceDuration"
    />

    <!-- CS Duration Dialog -->
    <ClientServiceQuarantineDurationDialog
      :show="csFormDialog"
      :is-editing="isEditingCs"
      :form-data="csFormData"
      :billing-periods="billingPeriods"
      :saving="savingCs"
      @close="csFormDialog = false"
      @save="saveCsDuration"
    />

    <!-- Delete Service Duration Confirmation -->
    <v-dialog v-model="deleteServiceDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this quarantine duration?
          <div class="mt-2" v-if="serviceToDelete">
            <strong>Service:</strong> {{ serviceToDelete.service_name }}<br>
            <strong>Type:</strong> {{ serviceToDelete.property_type }}<br>
            <strong>Duration:</strong> {{ serviceToDelete.quarantine_value }} {{ serviceToDelete.billing_period_name }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteServiceDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteServiceDuration" :loading="deletingService">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete CS Duration Confirmation -->
    <v-dialog v-model="deleteCsDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          Are you sure you want to delete this CS quarantine override?
          <div class="mt-2" v-if="csToDelete">
            <strong>CS #{{ csToDelete.client_service_id }}</strong><br>
            <strong>Type:</strong> {{ csToDelete.property_type }}<br>
            <strong>Duration:</strong> {{ csToDelete.quarantine_value }} {{ csToDelete.billing_period_name }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteCsDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteCsDuration" :loading="deletingCs">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import ServiceQuarantineDurationDialog from "@/components/dialogs/ServiceQuarantineDurationDialog.vue";
import ClientServiceQuarantineDurationDialog from "@/components/dialogs/ClientServiceQuarantineDurationDialog.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import {
  getServiceQuarantineDurations,
  addServiceQuarantineDuration,
  updateServiceQuarantineDuration,
  deleteServiceQuarantineDuration
} from "@/composables/useServiceQuarantineDurationsAPI";
import {
  getClientServiceQuarantineDurations,
  addClientServiceQuarantineDuration,
  updateClientServiceQuarantineDuration,
  deleteClientServiceQuarantineDuration
} from "@/composables/useClientServiceQuarantineDurationsAPI";
import { log } from "@/composables/useLog";
import store from "@/store";

export default defineComponent({
  name: 'ServiceQuarantineDurationsSettings',
  components: {
    NavBar,
    ServiceQuarantineDurationDialog,
    ClientServiceQuarantineDurationDialog
  },
  data() {
    return {
      activeTab: 'service-durations',
      loading: false,
      loadingCs: false,
      token: '',
      userLoaded: false,
      serviceDurations: [],
      csDurations: [],
      services: [],
      billingPeriods: [],

      // Service Duration Dialog
      serviceFormDialog: false,
      isEditingService: false,
      savingService: false,
      serviceFormData: { id: null, service_id: null, property_type: null, billing_period_id: null, quarantine_value: 0, active: true },

      // CS Duration Dialog
      csFormDialog: false,
      isEditingCs: false,
      savingCs: false,
      csFormData: { id: null, client_service_id: null, property_type: null, billing_period_id: null, quarantine_value: 0, active: true },

      // Delete Service
      deleteServiceDialog: false,
      deletingService: false,
      serviceToDelete: null,

      // Delete CS
      deleteCsDialog: false,
      deletingCs: false,
      csToDelete: null,

      // Snackbar
      snackbar: { show: false, message: '', color: 'success' },

      // Table headers
      serviceTableHeaders: [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Service', key: 'service_name', width: '25%' },
        { title: 'Property Type', key: 'property_type', width: '15%' },
        { title: 'Quarantine Duration', key: 'quarantine_display', width: '20%' },
        { title: 'Status', key: 'active', align: 'center', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '12%' }
      ],
      csTableHeaders: [
        { title: 'ID', key: 'id', width: '5%' },
        { title: 'Client Service', key: 'client_service_id', width: '30%' },
        { title: 'Property Type', key: 'property_type', width: '12%' },
        { title: 'Quarantine Duration', key: 'quarantine_display', width: '18%' },
        { title: 'Status', key: 'active', align: 'center', width: '10%' },
        { title: 'Actions', key: 'actions', sortable: false, width: '12%' }
      ]
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color };
    },

    propertyTypeColor(type) {
      const colors = { buy: 'blue', rent: 'orange', transfer: 'purple' };
      return colors[type] || 'grey';
    },

    // ==================== Service Duration CRUD ====================

    async loadData() {
      this.loading = true;
      try {
        const result = await getServiceQuarantineDurations(this.token);
        if (!result.error) {
          this.serviceDurations = result.durations || [];
          this.services = result.services || [];
          this.billingPeriods = result.billing_periods || [];
        } else {
          this.showSnackbar(result.message || 'Error loading data', 'error');
        }
      } catch (err) {
        log("Error loading data:", err);
        this.showSnackbar('Error loading data', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddServiceDialog() {
      this.isEditingService = false;
      this.serviceFormData = { id: null, service_id: null, property_type: null, billing_period_id: null, quarantine_value: 0, active: true };
      this.serviceFormDialog = true;
    },

    openEditServiceDialog(item) {
      this.isEditingService = true;
      this.serviceFormData = {
        id: item.id, service_id: item.service_id, property_type: item.property_type,
        billing_period_id: item.billing_period_id, quarantine_value: item.quarantine_value, active: item.active
      };
      this.serviceFormDialog = true;
    },

    async saveServiceDuration(data) {
      this.savingService = true;
      try {
        let result;
        if (this.isEditingService) {
          result = await updateServiceQuarantineDuration(this.token, data);
        } else {
          result = await addServiceQuarantineDuration(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditingService ? 'Duration updated' : 'Duration added');
          this.serviceFormDialog = false;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error saving', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error saving', 'error');
      } finally {
        this.savingService = false;
      }
    },

    async toggleServiceActive(item) {
      this.loading = true;
      try {
        const result = await updateServiceQuarantineDuration(this.token, { id: item.id, active: !item.active });
        if (!result.error) {
          this.showSnackbar(`Duration ${!item.active ? 'activated' : 'deactivated'}`);
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error updating', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error updating', 'error');
      } finally {
        this.loading = false;
      }
    },

    confirmDeleteService(item) {
      this.serviceToDelete = item;
      this.deleteServiceDialog = true;
    },

    async deleteServiceDuration() {
      if (!this.serviceToDelete) return;
      this.deletingService = true;
      try {
        const result = await deleteServiceQuarantineDuration(this.token, this.serviceToDelete.id);
        if (!result.error) {
          this.showSnackbar('Duration deleted');
          this.deleteServiceDialog = false;
          this.serviceToDelete = null;
          await this.loadData();
        } else {
          this.showSnackbar(result.message || 'Error deleting', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error deleting', 'error');
      } finally {
        this.deletingService = false;
      }
    },

    // ==================== CS Duration CRUD ====================

    async loadCsData() {
      this.loadingCs = true;
      try {
        const result = await getClientServiceQuarantineDurations(this.token);
        if (!result.error) {
          this.csDurations = result.durations || [];
          if (result.billing_periods) {
            this.billingPeriods = result.billing_periods;
          }
        } else {
          this.showSnackbar(result.message || 'Error loading CS data', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error loading CS data', 'error');
      } finally {
        this.loadingCs = false;
      }
    },

    openAddCsDialog() {
      this.isEditingCs = false;
      this.csFormData = { id: null, client_service_id: null, property_type: null, billing_period_id: null, quarantine_value: 0, active: true };
      this.csFormDialog = true;
    },

    openEditCsDialog(item) {
      this.isEditingCs = true;
      this.csFormData = {
        id: item.id, client_service_id: item.client_service_id, property_type: item.property_type,
        billing_period_id: item.billing_period_id, quarantine_value: item.quarantine_value, active: item.active
      };
      this.csFormDialog = true;
    },

    async saveCsDuration(data) {
      this.savingCs = true;
      try {
        let result;
        if (this.isEditingCs) {
          result = await updateClientServiceQuarantineDuration(this.token, data);
        } else {
          result = await addClientServiceQuarantineDuration(this.token, data);
        }
        if (!result.error) {
          this.showSnackbar(this.isEditingCs ? 'CS override updated' : 'CS override added');
          this.csFormDialog = false;
          await this.loadCsData();
        } else {
          this.showSnackbar(result.message || 'Error saving', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error saving', 'error');
      } finally {
        this.savingCs = false;
      }
    },

    async toggleCsActive(item) {
      this.loadingCs = true;
      try {
        const result = await updateClientServiceQuarantineDuration(this.token, { id: item.id, active: !item.active });
        if (!result.error) {
          this.showSnackbar(`CS override ${!item.active ? 'activated' : 'deactivated'}`);
          await this.loadCsData();
        } else {
          this.showSnackbar(result.message || 'Error updating', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error updating', 'error');
      } finally {
        this.loadingCs = false;
      }
    },

    confirmDeleteCs(item) {
      this.csToDelete = item;
      this.deleteCsDialog = true;
    },

    async deleteCsDuration() {
      if (!this.csToDelete) return;
      this.deletingCs = true;
      try {
        const result = await deleteClientServiceQuarantineDuration(this.token, this.csToDelete.id);
        if (!result.error) {
          this.showSnackbar('CS override deleted');
          this.deleteCsDialog = false;
          this.csToDelete = null;
          await this.loadCsData();
        } else {
          this.showSnackbar(result.message || 'Error deleting', 'error');
        }
      } catch (err) {
        this.showSnackbar('Error deleting', 'error');
      } finally {
        this.deletingCs = false;
      }
    }
  },

  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await Promise.all([this.loadData(), this.loadCsData()]);
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
